import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Zod from 'zod';
import { useTranslation } from 'react-i18next';
import DialogContentText from '@mui/material/DialogContentText';

import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { TextField } from 'common/forms/Field/TextField';

import { parseEmbedInput, IEmbedValue } from './utils';

interface IProps {
    open: boolean;
    initialValue?: string;
    onClose(): void;
    onSubmit(value: IEmbedValue): void;
}

export const EmbedDialog = ({
    open,
    initialValue,
    onClose,
    onSubmit,
}: IProps) => {
    const [translate] = useTranslation();

    const schema = Zod.object({
        input: Zod.string()
            .min(1, translate('validate.required'))
            .refine((value) => !!parseEmbedInput(value), {
                message: translate('editor.embedInputNotValid'),
            }),
    });

    const { control, handleSubmit, reset } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { input: initialValue || '' },
    });

    // Reset only on the closed-to-open transition, not on every
    // `initialValue` change while already open — otherwise an unrelated
    // update to the source value would clobber in-progress user input.
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) reset({ input: initialValue || '' });
        wasOpen.current = open;
    }, [open, initialValue, reset]);

    const submit = handleSubmit(({ input }) => {
        const value = parseEmbedInput(input);

        if (!value) return;

        onSubmit(value);
    });

    return (
        <AlertDialog
            fullWidth
            actions={
                <>
                    <Button
                        autoFocus
                        color="primary"
                        variant="contained"
                        onClick={submit}
                    >
                        {translate('save')}
                    </Button>
                    <Button variant="contained" onClick={onClose}>
                        {translate('cancel')}
                    </Button>
                </>
            }
            maxWidth="sm"
            open={open}
            title={translate('editor.embedDialogTitle')}
            onClose={onClose}
        >
            <DialogContentText color="text.primary" variant="body2">
                {translate('editor.embedDialogDescription')}
            </DialogContentText>
            <TextField
                autoFocus
                multiline
                control={control}
                minRows={3}
                name="input"
                placeholder={translate('editor.embedInputPlaceholder')}
            />
        </AlertDialog>
    );
};
